import * as React from "react";
import { Link } from "react-router-dom";
import {
  Space,
  Input,
  Popconfirm,
  Table,
  Skeleton,
  Spin,
  Form,
  message,
  Modal,
  Tag,
  Select,
  Tooltip,
  Badge,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { PlusButton } from "components/PlusButton";
import {
  MLModelApiCommunicator,
  formatFullDateFromTimeStamp,
  useAmplifyOrgSubscription,
} from "api";
import { addOrUpdateObjectInList } from "utils";
import { ListPageLayout } from "layouts";
import { useSearch } from "hooks";

const { Search, TextArea } = Input;
const { Option } = Select;

/**
 * MLModelListPage Component
 * 
 * Displays a list of all ML models with their current status and basic information.
 * Provides functionality for viewing, searching, creating, and deleting ML models.
 * 
 * Features:
 * - Sortable table with pagination
 * - Real-time search functionality
 * - ML model creation modal with printer/material selection
 * - Delete confirmation with popconfirm
 * - Real-time updates via Amplify subscriptions
 * - Status tracking and display
 * - Dataset association management
 * 
 * @returns {JSX.Element} The ML model list page component
 */
export function MLModelListPage() {
  const [mlModelApiCommunicator] = React.useState(new MLModelApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [messageApi, contextHolder] = message.useMessage();
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");
  const [addMLModelModalOpen, openAddMLModelModal] = React.useState(false);
  /** @type {ReturnType<typeof React.useState<import('models').MLModel[]>} */
  const [mlModels, setMlModels] = React.useState([]);
  const [printers, setPrinters] = React.useState([]);
  const [materials, setMaterials] = React.useState([]);

  const fetchPageData = React.useCallback(async () => {
    setLoading(true);
    try {
      // Make all API calls in parallel
      const [mlModelsRes, printersRes, materialsRes] = await Promise.all([
        mlModelApiCommunicator.list(),
        mlModelApiCommunicator.listPrinters(),
        mlModelApiCommunicator.listMaterials()
      ]);
      
      setMlModels(mlModelsRes.data.sort((a, b) => b.dateAdded - a.dateAdded));
      setPrinters(printersRes.data);
      setMaterials(materialsRes.data);
    } catch (err) {
      // console.error(err);
      messageApi.error(err.messagee);
    }
    setLoading(false);
  }, [messageApi, mlModelApiCommunicator]);

  useAmplifyOrgSubscription((update) => {
    if (update.type === "mlmodel" && !loading) {
      const newMlModels = addOrUpdateObjectInList(
        mlModels,
        update.data,
        "mlModelId"
      );
      setMlModels(newMlModels.sort((a, b) => b.dateAdded - a.dateAdded));
    }
  });

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const {
    query: searchTerm,
    setQuery: setSearchTerm,
    loading: isSearching,
    results: searchResults,
    search,
  } = useSearch(
    true,
    500,
    "mlmodel",
    React.useCallback(
      (msg) => messageApi.error("Error searching: " + msg),
      [messageApi]
    )
  );

  const cancelDelete = React.useCallback(() => {
    setPendingDeleteId("");
  }, []);

  const confirmDelete = React.useCallback(
    async (datasetId) => {
      setPendingDeleteId("");
      setLoading(true);
      try {
        await mlModelApiCommunicator.delete(datasetId);
        fetchPageData();
      } catch (err) {
        messageApi.error("Error deleting dataset " + err.message);
      }
      setLoading(false);
    },
    [mlModelApiCommunicator, fetchPageData, messageApi]
  );

  const addModel = React.useCallback(
    async (values) => {
      openAddMLModelModal(false);
      setLoading(true);

      try {
        await mlModelApiCommunicator.create({
          mlModelName: values.modelName,
          friendlyName: values.friendlyName,
          description: values.description ? values.description : "",
          printerId: values.printerId,
          materialId: values.materialId,
          datasetIds: [],
        });
        fetchPageData();
      } catch (err) {
        messageApi.error("Error creating dataset " + err.message);
      }
      setLoading(false);
    },
    [mlModelApiCommunicator, messageApi, fetchPageData]
  );

  const handleSearchValueChange = (e) => {
    const { value: inputValue } = e.target;
    setSearchTerm(inputValue);
  };

  const columns = [
    {
      title: "Name",
      key: "modelName",
      render: (_, r) => (
        <Link to={`/mlmodels/${r.mlModelId}`}>{r.mlModelName}</Link>
      ),
    },
    {
      title: "Printer",
      key: "printerName",
      dataIndex: "printerName",
    },
    {
      title: "Material",
      key: "materialName",
      dataIndex: "materialName",
    },
    {
      title: "Tags",
      key: "tags",
      dataIndex: "tags",
      render: (tags) => (
        <>
          {tags.map((tag) => (
            <Tag color="blue" key={tag}>
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Published",
      dataIndex: "completedAt",
      key: "resultCompletedAt",
      render: (_, r) => (
        r.results.some(result => result.published) ? (
          <>
            <Badge status='success' text='Yes'/>
          </>
        ) : (
          <span>No</span>
        )
      ),
    },
    {
      title: "# Versions",
      key: "versions",
      render: (_, r) => <div>{r.results.length}</div>,
    },
    {
      title: "Date",
      key: "dateAdded",
      render: (_, r) => <div>{formatFullDateFromTimeStamp(r.dateAdded)}</div>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Space>
          {Array.isArray(r.results) && r.results.length > 0 ? (
            <Tooltip title="This item can't be deleted because it contains processed versions that may be in use.">
              <DeleteOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', cursor: 'not-allowed' }} />
            </Tooltip>
          ) : (
            <Popconfirm
              title="Delete this model?"
              description="Are you sure to delete this model? This action cannot be undone."
              open={r.mlModelId === pendingDeleteId}
              onConfirm={() => confirmDelete(r.mlModelId)}
              onCancel={cancelDelete}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined onClick={() => setPendingDeleteId(r.mlModelId)} style={{ cursor: 'pointer' }} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <ListPageLayout>
      {contextHolder}
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
        }}
      >
        {loading ? (
          <span>
            <Spin /> {loadingMessage}
          </span>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Space style={{ display: "flex", alignItems: "center" }}>
              <h3 style={{ paddingLeft: "16px" }}>ML Models</h3>
              <PlusButton onClick={() => openAddMLModelModal(true)} />
            </Space>
            <Space>
              <Search
                placeholder="search ml models"
                value={searchTerm}
                onChange={handleSearchValueChange}
                onSearch={search}
                loading={isSearching}
                style={{ width: 200 }}
              />
            </Space>
          </div>
        )}
        <Skeleton loading={loading}>
          {searchTerm && searchResults ? (
            <Table
              columns={columns}
              dataSource={searchResults}
              rowKey="mlModelId"
              locale={{
                emptyText: "No data matching your search term " + searchTerm,
              }}
            />
          ) : (
            <Table columns={columns} dataSource={mlModels} rowKey="mlModelId" />
          )}
        </Skeleton>
        <AddMLModelModal
          open={addMLModelModalOpen}
          onCancel={() => openAddMLModelModal(false)}
          onSubmit={addModel} 
          printers={printers}
          materials={materials}
        />
      </Space>
    </ListPageLayout>
  );
}

function AddMLModelModal({ open = false, onCancel, onSubmit, printers, materials }) {
  const [form] = Form.useForm();
  return (
    <Modal
      title="Add Model"
      open={open}
      onCancel={onCancel}
      okText="Add Model"
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
        }
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
            form.resetFields();
          })
          .catch((info) => {
            // console.error("validation failed:", info);
          });
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Model Name"
          name="modelName"
          rules={[{ required: true, message: "Model name is required" }]}
        >
          <Input placeholder="Model name" />
        </Form.Item>
        <Form.Item
          label="Friendly Name"
          name="friendlyName"
          rules={[{ required: true, message: "Friendly name is required" }]}
        >
          <Input placeholder="Friendly name" />
        </Form.Item>
        <Form.Item
          label="Printer"
          name="printerId"
          rules={[{ required: true, message: "Printer is required" }]}
        >
          <Select placeholder="Select a printer">
            <Option value="">Select a printer</Option>
            {printers.map((printer) => (
              <Option key={printer.printerId} value={printer.printerId}>
                {printer.printerName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Material"
          name="materialId"
          rules={[{ required: true, message: "Material is required" }]}
        >
          <Select placeholder="Select a material">
            <Option value="">Select a material</Option>
            {materials.map((material) => (
              <Option key={material.materialId} value={material.materialId}>
                {material.materialName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: false }]}
        >
          <TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
